import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import CircularProgress from '@material-ui/core/CircularProgress';

import { ServerImage } from '../../../../server/app';

import PhotoSliderStyles from './PhotoSlider.styles';

// Types
type Props = {
  device1: ServerImage;
  device2: ServerImage;
};

// Component
const PhotoSlider: React.FC<Props> = ({ device1, device2 }) => {
  // Local state
  const [loading, setLoading] = useState(true);
  const [firstImage, setFirstImage] = useState<string | undefined>();
  const [secondImage, setSecondImage] = useState<string | undefined>();

  // Refs
  const clickedRef = useRef(0);
  const containerX = useRef(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const overContainerRef = useRef<HTMLDivElement>(null);
  const overImageRef = useRef<HTMLImageElement>(null);
  const draggerRef = useRef<HTMLDivElement>(null);

  // Helpers
  const clearSlider = () => (clickedRef.current = 0);

  const slideMove = (pageX: number) => {
    const container = containerRef.current;
    const overContainer = overContainerRef.current;
    const dragger = draggerRef.current;

    if (container && overContainer && dragger) {
      // Exit if no longer onmove
      if (clickedRef.current === 0) {
        return false;
      }

      // Prevent going beyond the container
      let pos = pageX - containerX.current;

      if (pos < 0) {
        pos = 0;
      }

      if (pos > container.offsetWidth) {
        pos = container.offsetWidth;
      }

      // Resize slide
      overContainer.style.width = `${pos}px`;
      dragger.style.left = `${pos}px`;

      return true;
    }

    return false;
  };

  // Handlers & Callbacks
  const setMeasurements = () => {
    const container = containerRef.current;
    const overContainer = overContainerRef.current;
    const overImage = overImageRef.current;
    const dragger = draggerRef.current;

    if (container && overContainer && overImage && dragger) {
      // Get X position
      containerX.current = container.getBoundingClientRect().x;

      // Fix over image width
      overImage.style.width = `${container.offsetWidth}px`;

      // Initialise positions
      const centerX = container.offsetWidth / 2;

      overContainer.style.width = `${centerX}px`;
      dragger.style.left = `${centerX}px`;
    }
  };

  const slideReady = useCallback((e: MouseEvent | TouchEvent) => {
    const container = containerRef.current;

    if (container) {
      e.preventDefault();

      // Activate
      clickedRef.current = 1;

      // Listen for changes
      container.addEventListener('mousemove', e => slideMove(e.pageX));
      container.addEventListener('touchmove', e => slideMove(e.touches[0].pageX));
      container.addEventListener('mouseup', clearSlider);
      container.addEventListener('touchend', clearSlider);
      container.addEventListener('mouseleave', clearSlider);
    }
  }, []);

  // Effects
  useEffect(() => {
    const container = containerRef.current;
    const dragger = draggerRef.current;

    if (container && dragger) {
      // Set initial measurements
      setMeasurements();

      // Reset measurements on window change
      // TODO: Consider changing to requestAnimationFrame
      window.addEventListener('resize', setMeasurements);

      // Listen for slider drags
      dragger.addEventListener('mousedown', slideReady);
      dragger.addEventListener('touchstart', slideReady);

      // Finished loading
      setLoading(false);

      // Clean up
      return () => {
        window.removeEventListener('resize', setMeasurements);

        dragger.removeEventListener('mousedown', slideReady);
        dragger.removeEventListener('touchstart', slideReady);

        container.removeEventListener('mousemove', e => slideMove(e.pageX));
        container.removeEventListener('touchmove', e => slideMove(e.touches[0].pageX));
        container.removeEventListener('mouseup', clearSlider);
        container.removeEventListener('touchend', clearSlider);
        container.removeEventListener('mouseleave', clearSlider);
      };
    }
  }, [slideReady]);

  useEffect(() => {
    setLoading(true);

    const retrieveImages = async () => {
      // Retrieve
      const firstRes = await axios.get(`/api/image/?path=photos/${device1.path}`, {
        responseType: 'blob',
      });

      const secondRes = await axios.get(`/api/image/?path=photos/${device2.path}`, {
        responseType: 'blob',
      });

      // Create URL blob
      const first = URL.createObjectURL(firstRes.data);
      const second = URL.createObjectURL(secondRes.data);

      // Preload & set
      const image1 = new Image();

      image1.src = first;
      image1.onload = () => {
        setFirstImage(first);
      };

      const image2 = new Image();

      image2.src = second;
      image2.onload = () => {
        setSecondImage(second);
      };
    };

    retrieveImages();
  }, [device1, device2]);

  useEffect(() => {
    if (firstImage && secondImage) {
      setLoading(false);
    }
  }, [firstImage, secondImage]);

  // Render
  return (
    <PhotoSliderStyles ref={containerRef}>
      <div className="slide">
        <div style={{ backgroundImage: secondImage && `url(${secondImage})` }} />
      </div>

      <div className="slide over" ref={overContainerRef}>
        <div style={{ backgroundImage: firstImage && `url(${firstImage})` }} ref={overImageRef} />
      </div>

      <div className="dragger-container" ref={draggerRef}>
        <div className="dragger" />
      </div>

      <div className="loading-container" hidden={!loading}>
        <div className="loading">
          <CircularProgress />
        </div>
      </div>
    </PhotoSliderStyles>
  );
};

export default PhotoSlider;
